import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { Toaster } from "./components/ui/toaster";
import { TourProvider } from "./context/TourProvider";
import { SiteContentProvider } from "./context/SiteContentProvider";

const Home = lazy(() => import("./pages/Home"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const About = lazy(() => import("./pages/About"));
const Memberships = lazy(() => import("./pages/Memberships"));
const Amenities = lazy(() => import("./pages/Amenities"));
const Contact = lazy(() => import("./pages/Contact"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Resources = lazy(() => import("./pages/Resources"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminMock = lazy(() => import("./pages/admin/AdminMock"));

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" aria-busy="true">
    <div className="w-8 h-8 rounded-full border-2 border-camel/30 border-t-camel animate-spin" />
  </div>
);

function ScrollManager() {
  const { pathname, hash, search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Normalize legacy trailing-slash URLs (e.g. /blog/foo/) to the canonical no-slash form.
    if (pathname !== "/" && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, "") + search + hash, { replace: true });
      return;
    }
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash, search, navigate]);
  return null;
}

function AppShell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/admin/*" element={<AdminMock />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      <Header />
      <main>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/locations/:id" element={<LocationDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/memberships" element={<Memberships />} />
            <Route path="/memberships/:slug" element={<Memberships />} />
            <Route path="/amenities" element={<Amenities />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollManager />
          <SiteContentProvider>
            <TourProvider>
              <AppShell />
            </TourProvider>
          </SiteContentProvider>
          <Toaster />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
